import React, { useEffect, useState } from "react";
import { api, waLink } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Search, Phone, Mail, Building2, Trash2, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", address: "", notes: "" });
  const nav = useNavigate();
  const [toDelete, setToDelete] = useState(null);
  const remove = async () => {
    try {
      const { data } = await api.delete(`/customers/${toDelete.id}`);
      toast.success(`${toDelete.name} deleted${data.jobs ? ` • ${data.jobs} job(s) kept in history` : ""}`);
      setToDelete(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete customer"); }
  };
  const emptyForm = { name: "", phone: "", email: "", company: "", address: "", notes: "" };
  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ name: c.name || "", phone: c.phone || "", email: c.email || "", company: c.company || "", address: c.address || "", notes: c.notes || "" }); setOpen(true); };

  const load = () => api.get("/customers", { params: q ? { q } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.patch(`/customers/${editing}`, form); toast.success("Customer updated"); }
      else { await api.post("/customers", form); toast.success("Customer added"); }
      setOpen(false); setEditing(null); setForm(emptyForm);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Customers</h1>
          <p className="text-sm text-muted-foreground">{items.length} customers on record</p>
        </div>
        <Button onClick={openNew} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="add-customer-btn"><UserPlus className="w-4 h-4 mr-1" /> Add Customer</Button>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name *</Label><Input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="customer-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} data-testid="customer-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
              </div>
              <div><Label>Company</Label><Input value={form.company} onChange={e=>setForm({...form,company:e.target.value})} /></div>
              <div><Label>Address</Label><Textarea rows={2} value={form.address} onChange={e=>setForm({...form,address:e.target.value})} /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></div>
              <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="customer-submit">Save</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&load()} placeholder="Search name, phone, company..." className="pl-9" data-testid="customer-search" />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <Card key={c.id} onClick={()=>nav(`/jobs?q=${encodeURIComponent(c.name)}`)} className="p-4 cursor-pointer hover:shadow-md transition relative group" data-testid={`customer-card-${c.id}`}>
            <div className="absolute top-2 right-2 flex gap-1">
              <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="p-1.5 rounded text-muted-foreground hover:text-cyan-600 hover:bg-cyan-50 transition-colors" title="Edit customer" data-testid={`customer-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setToDelete(c); }} className="p-1.5 rounded text-muted-foreground hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Delete customer" data-testid={`customer-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="font-display font-bold pr-16">{c.name}</div>
            {c.company && <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><Building2 className="w-3 h-3" />{c.company}</div>}
            {c.phone && (
              <a href={waLink(c.phone, `Hi ${c.name}, this is Rozfa Printing.`)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                 className="text-sm text-emerald-600 hover:underline flex items-center gap-1" data-testid={`customer-wa-${c.id}`}>
                <Phone className="w-3 h-3" />{c.phone} <span className="text-xs">(WhatsApp)</span>
              </a>
            )}
            {c.email && <div className="text-sm text-muted-foreground flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{c.email}</div>}
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-muted-foreground">No customers</div>}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the customer record{toDelete?.company ? ` (${toDelete.company})` : ""}. Existing jobs are kept for records but will no longer be linked to this customer. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-customer-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-customer-confirm">Delete Customer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
